import { PartnersHero } from "~/components/partners-hero";
import {
  ActionIcon,
  Alert,
  Button,
  Center,
  Drawer,
  Group,
  Image,
  Input,
  InputBase,
  Loader,
  Paper,
  Pagination,
  Radio,
  RadioCard,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  useMantineTheme,
} from "@mantine/core";
import "react-international-phone/style.css";
import type { loader } from "./handlers.server";

import {
  useDebouncedValue,
  useDisclosure,
  useMediaQuery,
} from "@mantine/hooks";

import { Spotlight, spotlight, SpotlightActionData } from "@mantine/spotlight";

import { HeadersArgs, MetaFunction } from "@remix-run/node";

import {
  Link,
  useFetcher,
  useLoaderData,
  useLocation,
  useNavigate,
  useSearchParams
} from "@remix-run/react";

import {
  IconAdjustmentsHorizontal,
  IconExclamationCircle,
  IconSearch,
} from "@tabler/icons-react";

import { useEffect, useMemo, useRef, useState } from "react";

import placeholderPerson from "~/assets/misc/placeholder-person.png";

import { BookHealthCareHero } from "~/components/book-healthcare-hero";

import { BusinessReviewBadge } from "~/components/business-review-badge";

import { DoctorCard } from "~/components/doctor-card";

import { DoctorSuggestionModal } from "~/components/doctor-suggestion-modal";

import {
  DEFAULT_OG_IMAGE_EN,
  DEFAULT_OG_IMAGE_FR
} from "~/constants";

import { getCountryValues, getDoctorAvailabilityValues } from "~/data/dropdown-values";

import { specialtyLocationPages } from "~/data/specialty-location-pages";

import { TranslationKey, useTranslation } from "~/hooks/use-typed-translation";


import utilityClasses from "~/styles/utilities.module.scss";

import { ComboboxItemPrice, DoctorProfile, OkDataError } from "~/types";
import type { PrimarySpecialization } from "~/services/get-primary-specializations";

import { getCentralizedMeta } from "~/utils/meta-helper";

import { renderLoadingErrorData } from "~/utils/render-loading-error-data";

import classes from "~/routes/($lang)._index/book-doctor.module.scss";

export const meta: MetaFunction<typeof loader> = ({ data, location }) => {
  if (!data) return [];
  
  return getCentralizedMeta({
    title: data.title,
    description: data.description,
    image: data.partner?.logo ? new URL(data.partner.logo, data.origin).href : data.locale === "fr" ? DEFAULT_OG_IMAGE_FR : DEFAULT_OG_IMAGE_EN,
    url: data.origin + location.pathname,
    locale: data.locale,
    keywords: data.keywords,
    alternateLanguages: {
      en: `${data.origin}/en${data.partner ? `/partners/${data.partner.slug}/doctors` : ""}`,
      fr: `${data.origin}/fr${data.partner ? `/partners/${data.partner.slug}/doctors` : ""}`,
    }
  });
};

export const headers = ({ loaderHeaders }: HeadersArgs) => {
  loaderHeaders.set("Cache-Control", "private, no-store");
  return loaderHeaders;
};

export default function BookAppointment() {
  const theme = useMantineTheme();
  const { t, i18n } = useTranslation();
  const { state } = useLocation();
  const navigate = useNavigate();
  const isSmall = useMediaQuery(`(min-width: ${theme.breakpoints.sm})`);
  const isLarge1 = useMediaQuery(`(min-width: ${theme.breakpoints.lg1})`);
  const [searchParams, setSearchParams] = useSearchParams();
  // console.log(searchParams.get("q"));

  const loaderData = useLoaderData<typeof loader>();
  const { data, error, ok, country, currency, doctorSuggestionsEnabled, partner } = loaderData;
  // console.log("loader data:", data, "ok:", ok, "error:", error, "country:", country);

  const [serviceType, setServiceType] = useState<ComboboxItemPrice | null>(
    state?.serviceType ?? null
  );
  const [search, setSearch] = useState(searchParams.get("q") ?? "");
  const [mobileSearch, setMobileSearch] = useState(searchParams.get("q") ?? "");
  const [debouncedSearch] = useDebouncedValue(search?.toLocaleLowerCase(), 400);
  const [filteredDoctors, setFilteredDoctors] = useState<DoctorProfile[]>(data?.profilesData ?? []);
  const [page, setPage] = useState(1);
  const [opened, { open, close }] = useDisclosure(false);
  const [suggestionOpened, suggestionModal] = useDisclosure(false);
  const [suggestionSubmissionId, setSuggestionSubmissionId] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<string>(searchParams.get("country") ?? "all"); 
  const [language, setLanguage] = useState("all");
  const [yearsOfExperience, setYearsOfExperience] = useState("all");
  const [selectedSpecialization, setSelectedSpecialization] = useState<string | null>(null);
  const [modeOfService, setModeOfService] = useState(() => {
    const mode = searchParams.get("modeOfService");
    return mode === "teleconsultation" || mode === "homecare" ? mode : "all";
  });
  const [dateRange, setDateRange] = useState<string | null>("all-time");
  const [isNudgeDismissed, setIsNudgeDismissed] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);

  const locale = i18n.language === "fr" ? "fr" : "en";
  const primarySpecializations: PrimarySpecialization[] = loaderData.primarySpecializations ?? [];
  const specializationOptions = primarySpecializations
    .map(entry => ({ value: entry._id, label: entry.labels[locale] }))
    .sort((a, b) => a.label.localeCompare(b.label, locale));
  const specializationFilter = (
    <Select
      label={t("specialization-filter")}
      placeholder={t("all-specializations")}
      mt="1rem"
      data={specializationOptions}
      value={selectedSpecialization || null}
      onChange={setSelectedSpecialization}
      searchable
      clearable
      nothingFoundMessage={t("no-matching-specializations")}
      disabled={!specializationOptions.length}
      description={loaderData.primarySpecializations === null
        ? t("specializations-unavailable")
        : !specializationOptions.length ? t("no-specializations-available") : undefined}
    />
  );
  const countryOptions = getCountryValues(t, data?.countries ?? [], locale);
  const homepageHeading = partner?.name ?? (
    locale === "fr"
      ? "Médecins en ligne en Afrique francophone"
      : "Online doctors in Francophone Africa");
  const specialtyLinks = partner ? [] : specialtyLocationPages.slice(0, 6);

  // Sync search state with URL for persistence
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (selectedCountry !== "all") {
      params.set("country", selectedCountry);
    } else {
      params.delete("country");
    }
    
    if (search) {
      params.set("q", search);
    } else {
      params.delete("q");
    }

    if (modeOfService !== "all") params.set("modeOfService", modeOfService);
    else params.delete("modeOfService");

    const nextSearch = params.toString();
    if (nextSearch === searchParams.toString()) {
      return;
    }
    
    setSearchParams(params, { replace: true, preventScrollReset: true });
  }, [selectedCountry, search, modeOfService]);

  useEffect(() => {
    setHasMounted(true);
    // Check if user has already dismissed the nudge in this session
    const dismissed = sessionStorage.getItem("laruche_nudge_dismissed");
    if (dismissed) setIsNudgeDismissed(true);
  }, []);

  const dismissNudge = () => {
    sessionStorage.setItem("laruche_nudge_dismissed", "true");
    setIsNudgeDismissed(true);
  };

  const openDoctorSuggestion = () => {
    setSuggestionSubmissionId(
      globalThis.crypto?.randomUUID?.() ||
        `${Date.now()}-${Math.random().toString(36).slice(2)}`
    );
    suggestionModal.open();
  };

  const isFirstRender = useRef(true);
  const doctorProfilesFetcher = useFetcher<OkDataError<DoctorProfile[]> & { pagination?: import("~/types").Pagination | null }>();
  const hasActiveDoctorFilters =
    !!selectedSpecialization ||
    !!debouncedSearch ||
    !!serviceType?.value ||
    language !== "all" ||
    yearsOfExperience !== "all" ||
    modeOfService !== "all" ||
    dateRange !== "all-time" ||
    (selectedCountry && selectedCountry !== "all");
  const profilesRequestSucceeded = hasActiveDoctorFilters || page > 1
    ? doctorProfilesFetcher.data?.ok ?? ok.profiles
    : ok.profiles;

  useEffect(() => {
    if (doctorProfilesFetcher?.data?.data) {
      import.meta.env.DEV &&
        console.log("[DEV] doctor filter data:", doctorProfilesFetcher?.data);
      setFilteredDoctors(doctorProfilesFetcher.data.data);
    }
  }, [doctorProfilesFetcher?.data]);

  useEffect(() => { setPage(1); }, [serviceType, debouncedSearch, language, yearsOfExperience, modeOfService, dateRange, selectedCountry, selectedSpecialization, partner?.slug]);

  useEffect(() => {
    let queryString = `/api/filter-doctor-profiles?q=0${partner ? `&partnerSlug=${encodeURIComponent(partner.slug)}&page=${page}` : ""}`; // q=0 is just to help concatenate the other query params in a predictable way

    if (isFirstRender.current) {
      isFirstRender.current = false;
    }

    // If there are no active filters, the route loader already has the right
    // doctor list. Avoid a second fetch and avoid replacing the rendered cards
    // with a spinner after hydration/revalidation.
    if (!hasActiveDoctorFilters && page === 1 && data?.profilesData) {
      setFilteredDoctors(data.profilesData);
      return;
    }

    // Include selected country for filtering (initially auto-detected, can be changed by user)
    if (selectedCountry && selectedCountry !== "all") {
      queryString += `&country=${selectedCountry}`;
    }
    if (selectedSpecialization) {
      queryString += `&primarySpecializationId=${encodeURIComponent(selectedSpecialization)}`;
    }

    if (debouncedSearch) {
      queryString += `&search=${encodeURIComponent(debouncedSearch.trim())}`;
    }

    if (serviceType?.value) {
      queryString += `&serviceType=${
        serviceType.value === "all" ? "" : serviceType.value
      }`;
    }

    if (language && language !== "all") {
      queryString += `&language=${language}`;
    }

    if (yearsOfExperience && yearsOfExperience !== "all") {
      queryString += `&yearsOfExperience=${yearsOfExperience}`;
    }

    if (modeOfService && modeOfService !== "all") {
      queryString += `&modeOfService=${modeOfService}`;
    }

    if (dateRange && dateRange !== "all-time") {
      queryString += `&dateRange=${dateRange}`;
    }

    doctorProfilesFetcher.load(queryString);
    setFilteredDoctors([]); // Empty this first to prevent flash of previous doctors before the filtered ones are displayed
  }, [
    serviceType,
    debouncedSearch,
    data?.profilesData,
    language,
    yearsOfExperience,
    modeOfService,
    dateRange,
    selectedCountry, // Use selectedCountry (user's choice) not country (auto-detected)
    selectedSpecialization,
    hasActiveDoctorFilters,
    partner?.slug,
    page,
  ]);

  const actions: SpotlightActionData[] = useMemo(
    () => [
      {
        id: "doctors",
        group: "Doctors",
        actions: (data?.profilesData ?? []).map((doctor: DoctorProfile) => ({
          id: doctor?.slug,
          label: doctor?.name,
          // description: doctor?.specialization,
          onClick: () =>
            navigate({ pathname: `/${i18n.language}${partner ? `/partners/${partner.slug}/doctors` : "/doctor"}/${doctor?.slug}` }),
          leftSection: (
            <Image
              src={doctor?.image ?? placeholderPerson}
              alt={doctor?.name}
              h={30}
              w={30}
              width={30}
              height={30}
              radius={"100vmax"}
            />
          ),
        })),
      },
      {
        id: "specialties",
        group: "Specialties",
        actions: Array.from(
          new Set<string>(data?.specialtiesData ?? [])
        ).map(
            (specialty: string) => ({
              id: specialty,
              label: specialty,
              onClick: () => setSearch(specialty.toLocaleLowerCase()),
            })
          ),
      },
    ],

    [data?.profilesData, data?.specialtiesData, partner?.slug, i18n.language, navigate]
  );

  return (
    <div className={`${classes.section} ${partner ? classes["partner-directory"] : ""}`}>
      {loaderData.structuredData ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(loaderData.structuredData),
          }}
        />
      ) : null}
      {partner && <PartnersHero compact />}
      <div className={utilityClasses.container}>
        {partner && <Link className={classes["partner-back"]} to={`/${locale}/partners/${partner.slug}`}>← {t("go-back")}</Link>}
        <form className={classes.body}>
          {!partner && <BookHealthCareHero
            partnerSlug={partner?.slug}
            heading={homepageHeading}
            headingOrder={1}
          />}

          {!partner && <section className={classes["seo-intro"]}>
            <Text component="p" fz={{ base: 14, sm: 15 }} c="dimmed" ta="center">
              {t("homepage-seo-intro")}
            </Text>
            <BusinessReviewBadge mt="md" />
          </section>}

          <div className={classes["flex-content"]}>
            <Paper withBorder p="1rem" className={classes["filters-container"]}>
              <Title order={2} fw={600} fz={18}>
                {t("filters")}
              </Title>
              {specializationFilter}

              {/* <Select
                label={t("service-type")}
                mt={"1rem"}
                data={data?.consultationServicesData?.map((item: any) => ({
                  value: item.value,
                  label: item.label,
                }))}
                value={serviceType?.value ?? null}
                onChange={(val, option) => {
                  setServiceType({
                    ...option,
                  });
                }}
                clearable
                clearButtonProps={{
                  onClick: () => setServiceType(null),
                }}
                placeholder={t("select-service")}
                searchable
              /> */}
              <Select
                label={t("availability")}
                placeholder=""
                mt="1rem"
                data={getDoctorAvailabilityValues(t)}
                value={dateRange}
                onChange={setDateRange}
              />
              <Select
                label={t(partner ? "partner-location-filter" : "country")}
                placeholder={t("select-country")}
                mt="1rem"
                data={countryOptions}
                value={selectedCountry}
                onChange={(val) => setSelectedCountry(val || "all")}
              />
              <Stack gap={"7"} mt={"2rem"}>
                <Text component="p" fz={14} fw={500}>
                  {t("mode-of-service")}
                </Text>
                <Radio.Group value={modeOfService} onChange={setModeOfService}>
                  <Group gap={"xs"}>
                    <RadioCard
                      className={classes["radio-card"]}
                      w={"fit-content"}
                      radius={"xl"}
                      value={"all"}
                      fz={13}>
                      {t("all")}
                    </RadioCard>
                    <RadioCard
                      className={classes["radio-card"]}
                      w={"fit-content"}
                      radius={"xl"}
                      value={"homecare"}
                      fz={13}>
                      {t("homecare")}
                    </RadioCard>
                    <RadioCard
                      className={classes["radio-card"]}
                      w={"fit-content"}
                      radius={"xl"}
                      value={"teleconsultation"}
                      fz={13}>
                      {t("teleconsultation")}
                    </RadioCard>
                  </Group>
                </Radio.Group>
              </Stack>

              <Stack gap={"7"} mt={"2rem"}>
                <Text component="p" fz={14} fw={500}>
                  {t("language")}
                </Text>
                <Radio.Group value={language} onChange={setLanguage}>
                  <Group gap={"xs"}>
                    <RadioCard
                      className={classes["radio-card"]}
                      w={"fit-content"}
                      radius={"xl"}
                      value={"all"}
                      fz={13}>
                      {t("all")}
                    </RadioCard>
                    {data?.languages?.length
                      ? data.languages.map((language: string) => (
                          <RadioCard
                            key={language}
                            className={classes["radio-card"]}
                            w={"fit-content"}
                            radius={"xl"}
                            value={language.toLocaleLowerCase()}
                            fz={13}
                            tt="capitalize">
                            {t(language as TranslationKey)}
                          </RadioCard>
                        ))
                      : null}
                  </Group>
                </Radio.Group>
              </Stack>

              <Stack gap={"7"} mt={"2rem"}>
                <Text component="p" fz={14} fw={500}>
                  {t("years-of-experience")}
                </Text>
                <Radio.Group
                  value={yearsOfExperience}
                  onChange={setYearsOfExperience}>
                  <Group gap={"xs"}>
                    <RadioCard
                      className={classes["radio-card"]}
                      w={"fit-content"}
                      radius={"xl"}
                      value={"all"}
                      fz={13}>
                      {t("all")}
                    </RadioCard>
                    <RadioCard
                      className={classes["radio-card"]}
                      w={"fit-content"}
                      radius={"xl"}
                      value={"2"}
                      fz={13}>
                      2+
                    </RadioCard>
                    <RadioCard
                      className={classes["radio-card"]}
                      w={"fit-content"}
                      radius={"xl"}
                      value={"5"}
                      fz={13}>
                      5+
                    </RadioCard>
                    <RadioCard
                      className={classes["radio-card"]}
                      w={"fit-content"}
                      radius={"xl"}
                      value={"10"}
                      fz={13}>
                      10+
                    </RadioCard>
                    <RadioCard
                      className={classes["radio-card"]}
                      w={"fit-content"}
                      radius={"xl"}
                      value={"15"}
                      fz={13}>
                      15+
                    </RadioCard>
                  </Group>
                </Radio.Group>
              </Stack>

              <Group mt={"2.5rem"} justify="space-between">
                <Button
                  variant="transparent"
                  size="compact-sm"
                  onClick={() => {
                    setSelectedSpecialization(null);
                    setServiceType(null);
                    setSelectedCountry(partner ? "all" : countryOptions.some(option => option.value === country) ? country : "all");
                    setLanguage("all");
                    setYearsOfExperience("all");
                    setModeOfService("all");
                    setDateRange("all-time");
                  }}>
                  {t("reset-filters")}
                </Button>
              </Group>
            </Paper>

            <div className={classes["cards-container"]}>
              <Group align="end" gap={7}>
                <TextInput
                  display={{ base: "none", sm: "block" }}
                  mt={partner ? 0 : { base: "3rem", lg1: 0 }}
                  type="search"
                  placeholder={t("search-for-doctor-or-specialty")}
                  leftSection={<IconSearch size={18} />}
                  rightSection={
                    search !== "" ? (
                      <Input.ClearButton onClick={() => setSearch("")} />
                    ) : null
                  }
                  value={search}
                  onChange={val => setSearch(val.currentTarget.value as string)}
                  flex={"1 1"}
                  styles={{
                    input: {
                      paddingBlock: "1.5em",
                    },
                  }}
                />
                <InputBase
                  display={{ base: "block", sm: "none" }}
                  component="button"
                  type="button"
                  leftSection={<IconSearch size={18} />}
                  mt={partner ? "0" : "3rem"}
                  flex={"1 1"}
                  onClick={() => spotlight.open()}>
                  {search ? search : t("search")}
                </InputBase>

                <Spotlight
                  display={{ base: "block", sm: "none" }}
                  actions={actions}
                  nothingFound={
                    <Stack align="center" gap="xs" py="sm">
                      <Text size="sm" c="dimmed">
                        {t("nothing-found")}...
                      </Text>
                      {mobileSearch.trim() && (
                        <Button
                          type="button"
                          size="compact-sm"
                          variant="light"
                          onClick={() => {
                            setSearch(mobileSearch.trim());
                            spotlight.close();
                          }}>
                          {t("doctor-suggestion-search-platform")}
                        </Button>
                      )}
                    </Stack>
                  }
                  clearQueryOnClose={false}
                  highlightQuery
                  yOffset={30}
                  fz={14}
                  limit={20}
                  searchProps={{
                    leftSection: <IconSearch size={20} stroke={1.5} />,
                    placeholder: t("search-for-doctor-or-specialty"),
                    inputSize: "sm",
                    size: "md",
                    fz: 14,
                    type: "search",
                    onInput: e => setMobileSearch(e.currentTarget.value),
                    onKeyDown: e => {
                      if (e.key === "Enter") {
                        setSearch(e.currentTarget.value);
                        spotlight.close();
                      }
                    },
                  }}
                  scrollable
                  maxHeight={300}
                />

                <ActionIcon
                  variant="outline"
                  flex={"1 0"}
                  maw={30}
                  size={isSmall ? "xl" : "lg"}
                  display={{ base: "block", lg1: "none" }}
                  onClick={open}
                  title={t("filters")}
                  aria-label={t("filters")}>
                  <IconAdjustmentsHorizontal size={18} />
                </ActionIcon>

                <Drawer
                  display={{ base: "block", lg1: "none" }}
                  size={isSmall ? 400 : 600}
                  position={isSmall ? "right" : "bottom"}
                  radius={isSmall ? "15" : "15 15 0 0"}
                  offset={isSmall ? 8 : 0}
                  opened={isLarge1 ? false : opened}
                  onClose={close}
                  title={
                    <Text span fz={16} fw={500}>
                      {t("filters")}
                    </Text>
                  }
                  withCloseButton>
                  {specializationFilter}
                  {/* <Select
                    label={t("service-type")}
                    data={data?.consultationServicesData?.map((item: any) => ({
                      value: item.value,
                      label: item.label,
                    }))}
                    value={serviceType?.value ?? null}
                    onChange={(_, option) => {
                      setServiceType({
                        ...option,
                      });
                    }}
                    clearable
                    clearButtonProps={{
                      onClick: () => setServiceType(null),
                    }}
                    placeholder={t("select-service")}
                    searchable
                  /> */}
                  <Select
                    label={t("availability")}
                    // mt="2rem"
                    data={getDoctorAvailabilityValues(t)}
                    value={dateRange}
                    onChange={setDateRange}
                  />
                  <Select
                    label={t(partner ? "partner-location-filter" : "country")}
                    placeholder={t("select-country")}
                    mt="1rem"
                    data={countryOptions}
                    value={selectedCountry}
                    onChange={(val) => setSelectedCountry(val || "CI")}
                  />

                  <Stack gap={"7"} mt={"2rem"}>
                    <Text component="p" fz={14} fw={500}>
                      {t("mode-of-service")}
                    </Text>
                    <Radio.Group
                      value={modeOfService}
                      onChange={setModeOfService}>
                      <Group gap={"xs"}>
                        <RadioCard
                          className={classes["radio-card"]}
                          w={"fit-content"}
                          radius={"xl"}
                          value={"all"}
                          fz={13}>
                          {t("all")}
                        </RadioCard>
                        <RadioCard
                          className={classes["radio-card"]}
                          w={"fit-content"}
                          radius={"xl"}
                          value={"homecare"}
                          fz={13}>
                          {t("homecare")}
                        </RadioCard>
                        <RadioCard
                          className={classes["radio-card"]}
                          w={"fit-content"}
                          radius={"xl"}
                          value={"teleconsultation"}
                          fz={13}>
                          {t("teleconsultation")}
                        </RadioCard>
                      </Group>
                    </Radio.Group>
                  </Stack>

                  <Stack gap={"7"} mt={"2rem"}>
                    <Text component="p" fz={14} fw={500}>
                      {t("language")}
                    </Text>
                    <Radio.Group value={language} onChange={setLanguage}>
                      <Group gap={"xs"}>
                        <RadioCard
                          className={classes["radio-card"]}
                          w={"fit-content"}
                          radius={"xl"}
                          value={"all"}
                          fz={13}>
                          {t("all")}
                        </RadioCard>
                        {data?.languages?.length
                          ? data.languages.map((language: string) => (
                              <RadioCard
                                key={language}
                                className={classes["radio-card"]}
                                w={"fit-content"}
                                radius={"xl"}
                                value={language.toLocaleLowerCase()}
                                fz={13}
                                tt="capitalize">
                                {t(language as TranslationKey)}
                              </RadioCard>
                            ))
                          : null}
                      </Group>
                    </Radio.Group>
                  </Stack>

                  <Stack gap={"7"} mt={"2rem"}>
                    <Text component="p" fz={14} fw={500}>
                      {t("years-of-experience")}
                    </Text>
                    <Radio.Group
                      value={yearsOfExperience}
                      onChange={setYearsOfExperience}>
                      <Group gap={"xs"}>
                        <RadioCard
                          className={classes["radio-card"]}
                          w={"fit-content"}
                          radius={"xl"}
                          value={"all"}
                          fz={13}>
                          {t("all")}
                        </RadioCard>
                        <RadioCard
                          className={classes["radio-card"]}
                          w={"fit-content"}
                          radius={"xl"}
                          value={"2"}
                          fz={13}>
                          2+
                        </RadioCard>
                        <RadioCard
                          className={classes["radio-card"]}
                          w={"fit-content"}
                          radius={"xl"}
                          value={"5"}
                          fz={13}>
                          5+
                        </RadioCard>
                        <RadioCard
                          className={classes["radio-card"]}
                          w={"fit-content"}
                          radius={"xl"}
                          value={"10"}
                          fz={13}>
                          10+
                        </RadioCard>
                        <RadioCard
                          className={classes["radio-card"]}
                          w={"fit-content"}
                          radius={"xl"}
                          value={"15"}
                          fz={13}>
                          15+
                        </RadioCard>
                      </Group>
                    </Radio.Group>
                  </Stack>

                  <Group
                    pos={partner ? "static" : "absolute"}
                    bottom={"1rem"}
                    right="1rem"
                    left="1rem"
                    mt={partner ? "xl" : "auto"}
                    justify="space-between">
                    <Button
                      variant="transparent"
                      size="compact-sm"
                      onClick={() => {
                        setSelectedSpecialization(null);
                        setServiceType(null);
                        setSelectedCountry(partner ? "all" : countryOptions.some(option => option.value === country) ? country : "all");
                        setLanguage("all");
                        setYearsOfExperience("all");
                        setModeOfService("all");
                        setDateRange("all-time");
                      }}>
                      {t("reset-filters")}
                    </Button>
                    <Button variant="filled" onClick={close}>
                      {t("apply-filters")}
                    </Button>
                  </Group>
                </Drawer>
              </Group>

              {/* Country Auto-Detection Banner */}
              {hasMounted && selectedCountry === "all" && !isNudgeDismissed && (
                <Alert
                  variant="light"
                  color={partner ? "primary" : "blue"}
                  radius="md"
                  mt="1rem"
                  withCloseButton
                  onClose={dismissNudge}
                  styles={{
                    root: {
                      backgroundColor: partner ? "var(--partner-surface)" : "rgba(59, 130, 246, 0.05)",
                      border: partner ? "1px solid var(--partner-border)" : "1px solid rgba(59, 130, 246, 0.2)",
                    },
                  }}>
                  <Stack gap="sm">
                    <Text fz={14} c={partner ? "var(--partner-focus)" : "blue.7"}>
                      {t("see-doctors-in-your-country")}
                    </Text>
                    <Group gap="xs" wrap="nowrap">
                      <Button
                        size="compact-sm"
                        variant="filled"
                        color={partner ? "primary" : "blue"}
                        onClick={() => {
                          setSelectedCountry(partner ? "all" : countryOptions.some(option => option.value === country) ? country : "all");
                        }}>
                        {t("filter-by-x", {
                          country:
                            countryOptions.find(c => c.value === country)
                              ?.label || country,
                        })}
                      </Button>
                    </Group>
                  </Stack>
                </Alert>
              )}

              {partner && <Group mt="lg" justify="space-between"><Text size="sm" c="dimmed" aria-live="polite">{t("partner-result-count", { count: filteredDoctors?.length ?? 0 })}</Text>{hasActiveDoctorFilters && <Button variant="subtle" size="xs" type="button" onClick={() => { setSelectedSpecialization(null); setServiceType(null); setSelectedCountry("all"); setSearch(""); setLanguage("all"); setYearsOfExperience("all"); setModeOfService("all"); setDateRange("all-time"); }}>{t("partner-clear-filters")}</Button>}</Group>}
              {partner && hasActiveDoctorFilters && <Group gap="xs" mt="sm">
                {[
                  selectedCountry !== "all" ? { label: countryOptions.find(option => option.value === selectedCountry)?.label || selectedCountry, clear: () => setSelectedCountry("all") } : null,
                  language !== "all" ? { label: t(language.toLowerCase() as TranslationKey), clear: () => setLanguage("all") } : null,
                  modeOfService !== "all" ? { label: t(modeOfService as TranslationKey), clear: () => setModeOfService("all") } : null,
                  yearsOfExperience !== "all" ? { label: `${t("years-of-experience")}: ${yearsOfExperience}+`, clear: () => setYearsOfExperience("all") } : null,
                  dateRange && dateRange !== "all-time" ? { label: getDoctorAvailabilityValues(t).find(option => option.value === dateRange)?.label || dateRange, clear: () => setDateRange("all-time") } : null,
                  selectedSpecialization ? { label: t("specialization-filter"), clear: () => setSelectedSpecialization(null) } : null,
                  serviceType?.value ? { label: serviceType.label, clear: () => setServiceType(null) } : null,
                ].filter(item => item !== null).map((item,index) => <Button key={index} type="button" variant="light" size="compact-xs" onClick={item.clear} aria-label={`${t("reset-filters")}: ${item.label}`}>{item.label} ×</Button>)}
              </Group>}
              {/* Active Filter Header */}
              {selectedCountry !== "all" && (
                <Group justify="space-between" mt="1.5rem" align="center">
                  <Text fz={15} fw={500} c="dimmed">
                    {t("showing-specialists-in")}:{" "}
                    <Text component="span" c="blue.7" fw={700}>
                      {countryOptions.find(c => c.value === selectedCountry)?.label}
                    </Text>
                  </Text>
                  <Button
                    variant="transparent"
                    size="compact-xs"
                    c="gray.6"
                    onClick={() => setSelectedCountry("all")}
                    leftSection={<IconSearch size={14} />}>
                    {t("all-countries")}
                  </Button>
                </Group>
              )}

              <Group
                className={partner ? classes["partner-results"] : undefined}
                mt={selectedCountry === "all" ? "1.5rem" : "1rem"}
                gap={"md"}
                justify="space-between"
                align="start">
                {renderLoadingErrorData({
                  isLoading: doctorProfilesFetcher.state === "loading",
                  loadingComponent: (
                    <Center w="100%">
                      <Loader size={30} />
                    </Center>
                  ),
                  isError: !profilesRequestSucceeded,
                  errorComponent: (
                    <Alert
                      w={"100%"}
                      mt="1rem"
                      variant="light"
                      color={"red"}
                      radius={"sm"}
                      icon={<IconExclamationCircle />}
                      title={
                        <Text c={"red"} fw={500} fz={14}>
                          {t("oops")}
                        </Text>
                      }>
                      <Text fz={14} c={"red"}>
                        {t("doctors-error")}
                      </Text>
                    </Alert>
                  ),
                  isSuccess: profilesRequestSucceeded,
                  dataLength:
                    filteredDoctors?.length ??
                    doctorProfilesFetcher?.data?.data ??
                    0,
                  emptyDataComponent: (
                    debouncedSearch?.trim() && doctorSuggestionsEnabled ? (
                      <Paper
                        withBorder
                        radius="lg"
                        className={classes["doctor-suggestion-card"]}
                        w="100%">
                        <Stack align="flex-start" gap={0}>
                          <Title
                            order={3}
                            className={classes["doctor-suggestion-heading"]}>
                            {t("doctor-suggestion-empty-title")}
                          </Title>
                          <Text
                            className={classes["doctor-suggestion-description"]}>
                            {t("doctor-suggestion-empty-description")}
                          </Text>
                          <Button
                            type="button"
                            variant="outline"
                            color={partner ? "primary" : "blue"}
                            className={classes["doctor-suggestion-button"]}
                            onClick={openDoctorSuggestion}>
                            {t("doctor-suggestion-open")}
                          </Button>
                        </Stack>
                      </Paper>
                    ) : (
                      <Text fz={15} c="dimmed" ta="center">
                        {t("empty-doctors")}
                      </Text>
                    )
                  ),
                  dataComponent: filteredDoctors?.map((doctor: DoctorProfile) => (
                    <DoctorCard
                        partnerSlug={partner?.slug}
                        modeOfService={partner ? modeOfService : undefined}
                      search={debouncedSearch ?? ""}
                      key={doctor.id}
                      doctor={doctor}
                      currency={currency}
                    />
                  )),
                })}
              </Group>
              {partner && <Pagination mt="xl" value={page} onChange={setPage} disabled={doctorProfilesFetcher.state !== "idle"} total={(hasActiveDoctorFilters || page > 1 ? doctorProfilesFetcher.data?.pagination?.pageCount : loaderData.pagination?.pageCount) ?? 1} />}
            </div>
          </div>

          {!partner && <section className={classes["popular-specialties"]}>
            <Stack gap={6} align="center">
              <Title order={2} fw={600} fz={{ base: 20, sm: 24 }} ta="center">
                {t("homepage-popular-specialties-title")}
              </Title>
              <Text c="dimmed" fz={{ base: 14, sm: 15 }} ta="center">
                {t("homepage-popular-specialties-description")}
              </Text>
            </Stack>

            <Group justify="center" gap="xs" mt="lg">
              <Button
                component={Link}
                to={`/${locale}/specialites`}
                variant="light"
                color="primary"
                radius="xl"
                size="compact-md">
                {t("homepage-browse-specialties")}
              </Button>
              {specialtyLinks.map(page => (
                <Button
                  key={`${page.specialtySlug}-${page.citySlug}`}
                  component={Link}
                  to={`/${locale}/specialites/${page.specialtySlug}/${page.citySlug}`}
                  variant="subtle"
                  color="gray"
                  radius="xl"
                  size="compact-md">
                  {page.specialtyLabel[locale]}
                </Button>
              ))}
            </Group>
          </section>}
        </form>
      </div>
      <DoctorSuggestionModal
        opened={suggestionOpened}
        onClose={suggestionModal.close}
        locale={locale}
        submissionId={suggestionSubmissionId}
        searchQuery={search.trim()}
        filters={{
          primary_specialization_id: selectedSpecialization || "",
          country: selectedCountry,
          service_type: serviceType?.value || "all",
          language,
          years_of_experience: yearsOfExperience,
          mode_of_service: modeOfService,
          date_range: dateRange || "all-time",
        }}
      />
    </div>
  );
}
